/**
 * Ticket Requests Store
 *
 * Zustand store for managing HMC ticket requests.
 * Handles fetching, filtering, status updates, and event availability.
 *
 * Created: 2026-01-13
 */

import { create } from 'zustand';
import { apiClient } from '@shared/services/apiClient';
import { useNotificationStore } from '@shared/store/notificationStore';
import { logger } from '@shared/utils/logger';

// Types
export type ChoiceStatus = 'pending' | 'approved' | 'denied' | null;

export interface TicketRequest {
  id: number;
  request_id: string;
  event_id: number;
  event_name: string;
  event_date: string;
  event_venue: string | null;
  full_name: string;
  company: string;
  title: string;
  email: string;
  phone: string | null;
  ticket_count: number;
  guest_names: string[];
  special_request: boolean;
  special_request_note: string | null;
  second_choice_event: string | null;
  third_choice_event: string | null;
  wants_benefits_followup: boolean;
  is_standby_request: boolean;
  status: 'pending' | 'requested_approval' | 'approved' | 'denied' | 'waitlist' | 'fulfilled';
  status_notes: string | null;
  primary_choice_status: ChoiceStatus;
  backup_choice_status: ChoiceStatus;      // 2nd choice status
  third_choice_status: ChoiceStatus;       // 3rd choice status
  is_archived: boolean;
  archived_at: string | null;
  email_sent_at: string | null;
  email_delivered_at: string | null;
  email_opened_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface EventAvailability {
  event_id: number;
  event_name: string;
  event_date: string | null;
  event_venue: string | null;
  total_tickets: number;
  available_tickets: number;
  status: 'available' | 'limited' | 'sold_out' | 'cancelled';
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface RequestStats {
  total_requests: number;
  pending_count: number;
  requested_approval_count: number;
  approved_count: number;
  denied_count: number;
  waitlist_count: number;
  fulfilled_count: number;
  total_tickets_requested: number;
}

export interface RequestFilters {
  status: string | null;
  event_id: number | null;
  date_from: string | null;
  date_to: string | null;
  search: string;
  include_archived: boolean;
}

interface TicketRequestsState {
  // Data
  requests: TicketRequest[];
  total: number;
  selectedRequest: TicketRequest | null;
  stats: RequestStats | null;

  // Event availability
  eventAvailability: Map<number, EventAvailability>;
  allEventAvailability: EventAvailability[];

  // Pagination
  limit: number;
  offset: number;

  // Filters
  filters: RequestFilters;

  // Notification tracking - tracks request IDs we've seen to detect new ones
  knownRequestIds: Set<string>;
  isInitialLoad: boolean;

  // Loading/error states
  isLoading: boolean;
  isLoadingStats: boolean;
  isUpdating: boolean;
  error: string | null;

  // Actions
  fetchRequests: () => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchEventAvailability: (eventId: number) => Promise<void>;
  fetchAllEventAvailability: () => Promise<void>;
  updateRequestStatus: (requestId: string, status: string, notes?: string) => Promise<boolean>;
  updateChoiceStatus: (
    requestId: string,
    primaryStatus?: ChoiceStatus,
    secondChoiceStatus?: ChoiceStatus,    // was backupStatus (2nd choice)
    thirdChoiceStatus?: ChoiceStatus,     // 3rd choice status
    notes?: string
  ) => Promise<boolean>;
  archiveRequest: (requestId: string) => Promise<boolean>;
  unarchiveRequest: (requestId: string) => Promise<boolean>;
  updateReceivedAt: (requestId: string, receivedAt: string) => Promise<boolean>;
  updateEventAvailability: (eventId: number, data: Partial<EventAvailability>) => Promise<boolean>;
  updateTicketCount: (requestId: string, ticketCount: number) => Promise<boolean>;
  updateEventSelections: (
    requestId: string,
    eventId?: number,
    eventName?: string,
    eventDate?: string,
    eventVenue?: string | null,
    secondChoiceEvent?: string | null,
    thirdChoiceEvent?: string | null
  ) => Promise<boolean>;

  // Selection
  selectRequest: (request: TicketRequest | null) => void;

  // Filters
  setFilter: <K extends keyof RequestFilters>(key: K, value: RequestFilters[K]) => void;
  clearFilters: () => void;
  setSearch: (search: string) => void;

  // Pagination
  setPage: (offset: number) => void;
  setLimit: (limit: number) => void;

  // State management
  clearError: () => void;
  reset: () => void;
}

const initialFilters: RequestFilters = {
  status: null,
  event_id: null,
  date_from: null,
  date_to: null,
  search: '',
  include_archived: false,
};

export const useTicketRequestsStore = create<TicketRequestsState>((set, get) => ({
  // Initial state
  requests: [],
  total: 0,
  selectedRequest: null,
  stats: null,
  eventAvailability: new Map(),
  allEventAvailability: [],
  limit: 50,
  offset: 0,
  filters: { ...initialFilters },
  knownRequestIds: new Set(),
  isInitialLoad: true,
  isLoading: false,
  isLoadingStats: false,
  isUpdating: false,
  error: null,

  // Fetch requests with current filters
  fetchRequests: async () => {
    set({ isLoading: true, error: null });

    try {
      const { filters, limit, offset } = get();

      // Build query params
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.event_id) params.append('event_id', filters.event_id.toString());
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.include_archived) params.append('include_archived', 'true');
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await apiClient.get<{
        requests: TicketRequest[];
        total: number;
        limit: number;
        offset: number;
      }>(`/hmc/ticket-requests?${params.toString()}`);

      // Apply client-side search filter if needed
      let filteredRequests = response.data.requests;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredRequests = filteredRequests.filter(
          (req) =>
            req.full_name.toLowerCase().includes(searchLower) ||
            req.company.toLowerCase().includes(searchLower) ||
            req.email.toLowerCase().includes(searchLower) ||
            req.event_name.toLowerCase().includes(searchLower)
        );
      }

      // Detect new submissions (only after initial load)
      const { knownRequestIds, isInitialLoad } = get();
      const currentRequestIds = new Set<string>(filteredRequests.map((r) => r.request_id));

      if (!isInitialLoad && knownRequestIds.size > 0) {
        // Find new requests that we haven't seen before
        const newRequests = filteredRequests.filter((r) => !knownRequestIds.has(r.request_id));

        if (newRequests.length > 0) {
          // Emit notifications for new submissions
          const notificationStore = useNotificationStore.getState();
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          for (const req of newRequests) {
            // Check if the event date has passed
            const eventDate = req.event_date ? new Date(req.event_date) : null;
            const isEventPast = eventDate && eventDate < today;

            if (isEventPast) {
              // Emit warning notification for past event submission
              notificationStore.addNotification({
                type: 'hmc_past_event_submission',
                title: 'Past Event Registration',
                message: `${req.full_name} from ${req.company} registered for "${req.event_name}" which has already passed (${eventDate.toLocaleDateString()})`,
                severity: 'warning',
                actionUrl: '/hmc-events?tab=requests',
                actionLabel: 'View Request',
                metadata: {
                  request_id: req.request_id,
                  event_id: req.event_id,
                  full_name: req.full_name,
                  company: req.company,
                  event_date: req.event_date,
                },
              });
              logger.log('[TicketRequestsStore] Past event registration detected:', req.full_name, 'for', req.event_name);
            } else {
              // Normal new submission notification
              notificationStore.addNotification({
                type: 'hmc_new_submission',
                title: 'New Ticket Request',
                message: `${req.full_name} from ${req.company} requested ${req.ticket_count} ticket${req.ticket_count > 1 ? 's' : ''} for ${req.event_name}`,
                severity: 'info',
                actionUrl: '/hmc-events?tab=requests',
                actionLabel: 'View Request',
                metadata: {
                  request_id: req.request_id,
                  event_id: req.event_id,
                  full_name: req.full_name,
                  company: req.company,
                },
              });
            }
          }
          logger.log('[TicketRequestsStore] Emitted notifications for', newRequests.length, 'new requests');
        }
      }

      set({
        requests: filteredRequests,
        total: response.data.total,
        knownRequestIds: currentRequestIds,
        isInitialLoad: false,
        isLoading: false,
      });

      logger.log('[TicketRequestsStore] Fetched', filteredRequests.length, 'requests');
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to fetch requests:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch requests',
        isLoading: false,
      });
    }
  },

  // Fetch stats
  fetchStats: async () => {
    set({ isLoadingStats: true });

    try {
      const response = await apiClient.get<RequestStats>('/hmc/ticket-requests/stats');

      set({
        stats: response.data,
        isLoadingStats: false,
      });
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to fetch stats:', error);
      set({ isLoadingStats: false });
    }
  },

  // Fetch availability for a specific event
  fetchEventAvailability: async (eventId: number) => {
    try {
      const response = await apiClient.get<EventAvailability | null>(
        `/hmc/events/${eventId}/availability`
      );

      if (response.data) {
        const { eventAvailability } = get();
        const newMap = new Map(eventAvailability);
        newMap.set(eventId, response.data);
        set({ eventAvailability: newMap });
      }
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to fetch event availability:', error);
    }
  },

  // Fetch all event availability
  fetchAllEventAvailability: async () => {
    try {
      const response = await apiClient.get<EventAvailability[]>('/hmc/events/availability');

      // Build map from list
      const newMap = new Map<number, EventAvailability>();
      for (const avail of response.data) {
        newMap.set(avail.event_id, avail);
      }

      set({
        eventAvailability: newMap,
        allEventAvailability: response.data,
      });
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to fetch all event availability:', error);
    }
  },

  // Update request status
  updateRequestStatus: async (requestId: string, status: string, notes?: string) => {
    set({ isUpdating: true });

    try {
      await apiClient.patch(`/hmc/ticket-requests/${requestId}/status`, {
        status,
        notes,
      });

      // Update local state
      const { requests, selectedRequest } = get();
      const updatedRequests = requests.map((req) =>
        req.request_id === requestId
          ? { ...req, status: status as TicketRequest['status'], status_notes: notes || null }
          : req
      );

      const updatedSelected =
        selectedRequest?.request_id === requestId
          ? { ...selectedRequest, status: status as TicketRequest['status'], status_notes: notes || null }
          : selectedRequest;

      set({
        requests: updatedRequests,
        selectedRequest: updatedSelected,
        isUpdating: false,
      });

      // Refresh stats
      get().fetchStats();

      logger.log('[TicketRequestsStore] Status updated:', requestId, '->', status);
      return true;
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to update status:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update status',
        isUpdating: false,
      });
      return false;
    }
  },

  // Update choice status (primary/2nd choice/3rd choice)
  updateChoiceStatus: async (
    requestId: string,
    primaryStatus?: ChoiceStatus,
    secondChoiceStatus?: ChoiceStatus,    // was backupStatus (2nd choice)
    thirdChoiceStatus?: ChoiceStatus,     // 3rd choice status
    notes?: string
  ) => {
    set({ isUpdating: true });

    try {
      // Build payload - only include fields that are defined
      const payload: Record<string, unknown> = {};
      if (primaryStatus !== undefined) payload.primary_choice_status = primaryStatus;
      if (secondChoiceStatus !== undefined) payload.backup_choice_status = secondChoiceStatus;
      if (thirdChoiceStatus !== undefined) payload.third_choice_status = thirdChoiceStatus;
      if (notes !== undefined) payload.notes = notes;

      await apiClient.patch(`/hmc/ticket-requests/${requestId}/choice-status`, payload);

      // Update local state
      const { requests, selectedRequest } = get();

      // Find the request for notification purposes
      const targetRequest = requests.find((r) => r.request_id === requestId);

      const updatedRequests = requests.map((req) =>
        req.request_id === requestId
          ? {
              ...req,
              primary_choice_status: primaryStatus !== undefined ? primaryStatus : req.primary_choice_status,
              backup_choice_status: secondChoiceStatus !== undefined ? secondChoiceStatus : req.backup_choice_status,
              third_choice_status: thirdChoiceStatus !== undefined ? thirdChoiceStatus : req.third_choice_status,
              status_notes: notes !== undefined ? notes : req.status_notes,
            }
          : req
      );

      const updatedSelected =
        selectedRequest?.request_id === requestId
          ? {
              ...selectedRequest,
              primary_choice_status: primaryStatus !== undefined ? primaryStatus : selectedRequest.primary_choice_status,
              backup_choice_status: secondChoiceStatus !== undefined ? secondChoiceStatus : selectedRequest.backup_choice_status,
              third_choice_status: thirdChoiceStatus !== undefined ? thirdChoiceStatus : selectedRequest.third_choice_status,
              status_notes: notes !== undefined ? notes : selectedRequest.status_notes,
            }
          : selectedRequest;

      set({
        requests: updatedRequests,
        selectedRequest: updatedSelected,
        isUpdating: false,
      });

      // Emit notification for status changes (approved or denied)
      if (targetRequest) {
        const notificationStore = useNotificationStore.getState();

        // Helper to emit notification for a specific choice
        const emitStatusNotification = (
          status: ChoiceStatus,
          choiceType: 'primary' | 'backup' | 'third',
          choiceLabel: string
        ) => {
          if (status === 'approved' || status === 'denied') {
            const severity = status === 'approved' ? 'success' : 'warning';
            const statusText = status === 'approved' ? 'Approved' : 'Denied';

            notificationStore.addNotification({
              type: 'hmc_guest_status_changed',
              title: `${choiceLabel} Choice ${statusText}`,
              message: `${targetRequest.full_name}'s ${choiceLabel.toLowerCase()} choice for "${targetRequest.event_name}" was ${status}`,
              severity,
              actionUrl: '/hmc-events?tab=requests',
              actionLabel: 'View Request',
              metadata: {
                request_id: requestId,
                guest_name: targetRequest.full_name,
                event_name: targetRequest.event_name,
                event_date: targetRequest.event_date,
                status,
                choice_type: choiceType,
              },
            });
            logger.log(`[TicketRequestsStore] Emitted ${choiceLabel.toLowerCase()} choice status notification:`, status);
          }
        };

        // Check each choice type for status changes
        if (primaryStatus !== undefined) {
          emitStatusNotification(primaryStatus, 'primary', 'Primary');
        }
        if (secondChoiceStatus !== undefined) {
          emitStatusNotification(secondChoiceStatus, 'backup', '2nd');
        }
        if (thirdChoiceStatus !== undefined) {
          emitStatusNotification(thirdChoiceStatus, 'third', '3rd');
        }
      }

      logger.log('[TicketRequestsStore] Choice status updated:', requestId);
      return true;
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to update choice status:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update choice status',
        isUpdating: false,
      });
      return false;
    }
  },

  // Archive request
  archiveRequest: async (requestId: string) => {
    set({ isUpdating: true });

    try {
      await apiClient.post(`/hmc/ticket-requests/${requestId}/archive`);

      // Update local state - remove from list unless include_archived is true
      const { requests, selectedRequest, filters } = get();

      if (filters.include_archived) {
        // Update the record in place
        const updatedRequests = requests.map((req) =>
          req.request_id === requestId
            ? { ...req, is_archived: true, archived_at: new Date().toISOString() }
            : req
        );
        set({ requests: updatedRequests, isUpdating: false });
      } else {
        // Remove from list
        const updatedRequests = requests.filter((req) => req.request_id !== requestId);
        set({
          requests: updatedRequests,
          selectedRequest: selectedRequest?.request_id === requestId ? null : selectedRequest,
          isUpdating: false,
        });
      }

      // Refresh stats
      get().fetchStats();

      logger.log('[TicketRequestsStore] Request archived:', requestId);
      return true;
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to archive request:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to archive request',
        isUpdating: false,
      });
      return false;
    }
  },

  // Unarchive request
  unarchiveRequest: async (requestId: string) => {
    set({ isUpdating: true });

    try {
      await apiClient.post(`/hmc/ticket-requests/${requestId}/unarchive`);

      // Update local state
      const { requests, selectedRequest } = get();
      const updatedRequests = requests.map((req) =>
        req.request_id === requestId
          ? { ...req, is_archived: false, archived_at: null }
          : req
      );

      const updatedSelected =
        selectedRequest?.request_id === requestId
          ? { ...selectedRequest, is_archived: false, archived_at: null }
          : selectedRequest;

      set({
        requests: updatedRequests,
        selectedRequest: updatedSelected,
        isUpdating: false,
      });

      // Refresh stats
      get().fetchStats();

      logger.log('[TicketRequestsStore] Request unarchived:', requestId);
      return true;
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to unarchive request:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to unarchive request',
        isUpdating: false,
      });
      return false;
    }
  },

  // Update received at timestamp
  updateReceivedAt: async (requestId: string, receivedAt: string) => {
    set({ isUpdating: true });

    try {
      await apiClient.patch(`/hmc/ticket-requests/${requestId}/received-at`, {
        received_at: receivedAt,
      });

      // Update local state
      const { requests, selectedRequest } = get();
      const updatedRequests = requests.map((req) =>
        req.request_id === requestId ? { ...req, created_at: receivedAt } : req
      );

      const updatedSelected =
        selectedRequest?.request_id === requestId
          ? { ...selectedRequest, created_at: receivedAt }
          : selectedRequest;

      set({
        requests: updatedRequests,
        selectedRequest: updatedSelected,
        isUpdating: false,
      });

      logger.log('[TicketRequestsStore] Received timestamp updated:', requestId, '->', receivedAt);
      return true;
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to update received timestamp:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update received timestamp',
        isUpdating: false,
      });
      return false;
    }
  },

  // Update ticket count for a request
  updateTicketCount: async (requestId: string, ticketCount: number) => {
    set({ isUpdating: true });

    try {
      await apiClient.patch(`/hmc/ticket-requests/${requestId}/ticket-count`, {
        ticket_count: ticketCount,
      });

      // Update local state
      const { requests, selectedRequest } = get();
      const updatedRequests = requests.map((req) =>
        req.request_id === requestId ? { ...req, ticket_count: ticketCount } : req
      );

      const updatedSelected =
        selectedRequest?.request_id === requestId
          ? { ...selectedRequest, ticket_count: ticketCount }
          : selectedRequest;

      set({
        requests: updatedRequests,
        selectedRequest: updatedSelected,
        isUpdating: false,
      });

      logger.log('[TicketRequestsStore] Ticket count updated:', requestId, '->', ticketCount);
      return true;
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to update ticket count:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update ticket count',
        isUpdating: false,
      });
      return false;
    }
  },

  // Update event selections (primary event and backup choices)
  updateEventSelections: async (
    requestId: string,
    eventId?: number,
    eventName?: string,
    eventDate?: string,
    eventVenue?: string | null,
    secondChoiceEvent?: string | null,
    thirdChoiceEvent?: string | null
  ) => {
    set({ isUpdating: true });

    try {
      // Build payload - only include fields that are defined
      const payload: Record<string, unknown> = {};
      if (eventId !== undefined) payload.event_id = eventId;
      if (eventName !== undefined) payload.event_name = eventName;
      if (eventDate !== undefined) payload.event_date = eventDate;
      if (eventVenue !== undefined) payload.event_venue = eventVenue;
      // For backup choices, empty string means clear, null means no change
      if (secondChoiceEvent !== undefined) {
        payload.second_choice_event = secondChoiceEvent === null ? '' : secondChoiceEvent;
      }
      if (thirdChoiceEvent !== undefined) {
        payload.third_choice_event = thirdChoiceEvent === null ? '' : thirdChoiceEvent;
      }

      await apiClient.patch(`/hmc/ticket-requests/${requestId}/event-selections`, payload);

      // Update local state
      const { requests, selectedRequest } = get();
      const updatedRequests = requests.map((req) =>
        req.request_id === requestId
          ? {
              ...req,
              event_id: eventId !== undefined ? eventId : req.event_id,
              event_name: eventName !== undefined ? eventName : req.event_name,
              event_date: eventDate !== undefined ? eventDate : req.event_date,
              event_venue: eventVenue !== undefined ? eventVenue : req.event_venue,
              second_choice_event: secondChoiceEvent !== undefined ? (secondChoiceEvent || null) : req.second_choice_event,
              third_choice_event: thirdChoiceEvent !== undefined ? (thirdChoiceEvent || null) : req.third_choice_event,
            }
          : req
      );

      const updatedSelected =
        selectedRequest?.request_id === requestId
          ? {
              ...selectedRequest,
              event_id: eventId !== undefined ? eventId : selectedRequest.event_id,
              event_name: eventName !== undefined ? eventName : selectedRequest.event_name,
              event_date: eventDate !== undefined ? eventDate : selectedRequest.event_date,
              event_venue: eventVenue !== undefined ? eventVenue : selectedRequest.event_venue,
              second_choice_event: secondChoiceEvent !== undefined ? (secondChoiceEvent || null) : selectedRequest.second_choice_event,
              third_choice_event: thirdChoiceEvent !== undefined ? (thirdChoiceEvent || null) : selectedRequest.third_choice_event,
            }
          : selectedRequest;

      set({
        requests: updatedRequests,
        selectedRequest: updatedSelected,
        isUpdating: false,
      });

      logger.log('[TicketRequestsStore] Event selections updated:', requestId);
      return true;
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to update event selections:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update event selections',
        isUpdating: false,
      });
      return false;
    }
  },

  // Update event availability
  updateEventAvailability: async (eventId: number, data: Partial<EventAvailability>) => {
    set({ isUpdating: true });

    try {
      const response = await apiClient.put<EventAvailability>(
        `/hmc/events/${eventId}/availability`,
        data
      );

      // Update local state
      const { eventAvailability, allEventAvailability } = get();
      const newMap = new Map(eventAvailability);
      newMap.set(eventId, response.data);

      const newList = allEventAvailability.map((avail) =>
        avail.event_id === eventId ? response.data : avail
      );

      // Add if not exists
      if (!allEventAvailability.find((a) => a.event_id === eventId)) {
        newList.push(response.data);
      }

      set({
        eventAvailability: newMap,
        allEventAvailability: newList,
        isUpdating: false,
      });

      logger.log('[TicketRequestsStore] Event availability updated:', eventId);
      return true;
    } catch (error) {
      logger.error('[TicketRequestsStore] Failed to update availability:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update availability',
        isUpdating: false,
      });
      return false;
    }
  },

  // Selection
  selectRequest: (request) => {
    set({ selectedRequest: request });

    // Fetch availability for the selected request's event
    if (request) {
      get().fetchEventAvailability(request.event_id);
    }
  },

  // Filters
  setFilter: (key, value) => {
    const { filters } = get();
    set({
      filters: { ...filters, [key]: value },
      offset: 0, // Reset pagination when filter changes
    });
    // Auto-fetch on filter change
    get().fetchRequests();
  },

  clearFilters: () => {
    set({
      filters: { ...initialFilters },
      offset: 0,
    });
    get().fetchRequests();
  },

  setSearch: (search) => {
    const { filters } = get();
    set({
      filters: { ...filters, search },
    });
    // Debounce would be nice here, but for now just fetch
    get().fetchRequests();
  },

  // Pagination
  setPage: (offset) => {
    set({ offset });
    get().fetchRequests();
  },

  setLimit: (limit) => {
    set({ limit, offset: 0 });
    get().fetchRequests();
  },

  // State management
  clearError: () => set({ error: null }),

  reset: () => {
    set({
      requests: [],
      total: 0,
      selectedRequest: null,
      stats: null,
      eventAvailability: new Map(),
      allEventAvailability: [],
      limit: 50,
      offset: 0,
      filters: { ...initialFilters },
      knownRequestIds: new Set(),
      isInitialLoad: true,
      isLoading: false,
      isLoadingStats: false,
      isUpdating: false,
      error: null,
    });
  },
}));
